// ChatBot.jsx — agentic chat drawer.
//
// A floating button bottom-right opens a side drawer (full-screen sheet on
// mobile). Streams messages from Claude with tool use, inline approval cards
// for write tools, and a scrollable transcript.

import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Loader2, Check, Wrench, Trash2, Plus, Menu, Pencil, BookPlus, Maximize2, ExternalLink, ChevronLeft, ArrowUp } from "lucide-react";
import { runChatTurn, useChatHistory, formatCostCents, formatTokens } from "../lib/chat";
import { ChatBubble } from "./pronto";

// Glass surface atom for the message-input bar (matches pronto.jsx GLASS).
const GLASS_INPUT = "bg-[var(--pg)] border border-[var(--pg-border)] backdrop-blur-[20px]";

// Human-readable labels for tool calls. Gerund form while running; concise
// past-tense form once done, to read as the prototype's "✓ searched recipes"
// tool-step chips.
const TOOL_LABELS = {
  search_pantry: "Searching pantry",
  search_recipes: "Searching recipes",
  get_recipe: "Reading recipe",
  list_appliances: "Listing appliances",
  list_beans: "Listing beans",
  get_taste_profile: "Reading taste profile",
  get_recent_brews: "Reading recent brews",
  get_shopping_list: "Reading shopping list",
  add_shopping_item: "Add shopping item",
  mark_recipe_made: "Mark recipe made",
  toggle_favorite_recipe: "Toggle favorite",
  save_recipe: "Save recipe",
};

const TOOL_LABELS_DONE = {
  search_pantry: "read pantry",
  search_recipes: "searched recipes",
  get_recipe: "read recipe",
  list_appliances: "listed appliances",
  list_beans: "listed beans",
  get_taste_profile: "read taste profile",
  get_recent_brews: "read recent brews",
  get_shopping_list: "read shopping list",
  add_shopping_item: "added to shopping",
  mark_recipe_made: "logged as cooked",
  toggle_favorite_recipe: "toggled favorite",
  save_recipe: "saved recipe",
};

function describeWrite(name, input) {
  switch (name) {
    case "add_shopping_item":
      return <>Add <strong className="font-semibold text-pronto-ink">{input.text}</strong> to shopping list</>;
    case "mark_recipe_made":
      return <>Log this recipe as cooked{input.servings_made ? ` (${input.servings_made} servings)` : ""}</>;
    case "toggle_favorite_recipe":
      return <>Toggle favorite on this recipe</>;
    default:
      return <>{name}</>;
  }
}

export function ChatBot({ store }) {
  const [open, setOpen] = useState(false);
  // History lives at the parent so it survives drawer close/open AND
  // page reloads (via useChatHistory's localStorage backing). The meter
  // listens for chat-tagged AI usage events and accumulates session cost.
  const chat = useChatHistory(store.userId);

  return (
    <>
      {/* Position lifts above the fixed audio player when one is mounted: the
          PlayerBar sets --player-h on the document root while visible. The
          mobile breakpoint stacks above the bottom tab bar via the safe-bottom
          inset on the .md\:hidden nav (z-30) — the chat sits at z-40. */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ bottom: "calc(var(--player-h, 0px) + var(--chat-base, 1.5rem))" }}
        className="fixed right-5 md:right-6 z-40 w-12 h-12 rounded-full bg-pronto-navy text-white shadow-lg hover:bg-pronto-navy-deep transition-[bottom] duration-200 flex items-center justify-center [--chat-base:6rem] md:[--chat-base:1.5rem]"
        aria-label="Open Pronto chat"
      >
        {open ? <X size={20} strokeWidth={2.2} /> : <MessageCircle size={20} strokeWidth={2.2} />}
      </button>

      <ChatDrawer
        open={open}
        onClose={() => setOpen(false)}
        store={store}
        chat={chat}
      />
    </>
  );
}

// Standalone full-page chat — rendered by App at the `#chat` route, either from
// the drawer's "expand" control (same tab) or "open in new tab" (§F1). Owns its
// own history hook so the popped-out tab is a fully independent chat surface
// (it shares the same localStorage-backed conversations, so both stay in sync).
export function ChatFullPage({ store, onExit }) {
  const chat = useChatHistory(store.userId);
  return <ChatDrawer open fullPage onClose={onExit} store={store} chat={chat} />;
}

function ChatDrawer({ open, onClose, store, chat, fullPage = false }) {
  const {
    conversations, activeId, uiMessages: messages, setUiMessages: setMessages,
    apiHistoryRef, meter, newChat, selectChat, deleteChat, renameChat, clear: onClear,
  } = chat;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null); // { name, input, resolve }
  const [showList, setShowList] = useState(false); // mobile: chat-list overlay open?
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const activeConv = conversations.find(c => c.id === activeId) || null;
  const activeTitle = activeConv && activeConv.title ? activeConv.title : "New chat";
  const sortedConvs = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);

  // Switching chats mid-turn would tangle the in-flight abort controller with a
  // different transcript — block list actions while a turn is streaming.
  const handleSelect = (id) => { if (busy) return; selectChat(id); setShowList(false); };
  const handleNew = () => { if (busy) return; newChat(); setShowList(false); };
  const handleDelete = (id) => {
    if (busy) return;
    const conv = conversations.find(c => c.id === id);
    if (conv && conv.uiMessages.length > 0 && !window.confirm("Delete this conversation?")) return;
    deleteChat(id);
  };
  const handleRename = (id, title) => renameChat(id, title);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Cancel in-flight on unmount/close
  useEffect(() => {
    if (!open && abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setBusy(false);
      setPendingApproval(null);
    }
  }, [open]);

  const requestApproval = useCallback((name, input) => {
    return new Promise((resolve) => {
      setPendingApproval({ name, input, resolve });
    });
  }, []);

  // decision is `false` (deny) or a truthy value (approve). Write cards with
  // editable fields pass an object payload (e.g. { title }) that runChatTurn
  // merges into the tool input before executing.
  const resolveApproval = (decision) => {
    if (!pendingApproval) return;
    pendingApproval.resolve(decision);
    const approved = !!decision;
    setMessages(prev => prev.map(m => m.id === pendingApproval.uiId ? { ...m, decided: approved ? "approved" : "denied" } : m));
    setPendingApproval(null);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (!store.apiKey) {
      setMessages(prev => [
        ...prev,
        { id: crypto.randomUUID(), role: "system", text: "Chat needs an Anthropic key — add one in Settings. (Chat is Anthropic-only; other AI features work on any provider.)" },
      ]);
      return;
    }
    setInput("");
    setBusy(true);

    const userMsg = { id: crypto.randomUUID(), role: "user", text };
    setMessages(prev => [...prev, userMsg]);
    apiHistoryRef.current.push({ role: "user", content: text });

    // Set up an assistant container we'll mutate as deltas stream
    const assistantId = crypto.randomUUID();
    setMessages(prev => [...prev, { id: assistantId, role: "assistant", text: "", events: [] }]);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await runChatTurn({
        apiKey: store.apiKey,
        messages: apiHistoryRef.current,
        store,
        signal: ac.signal,
        requestApproval: async (name, input) => {
          // Push an approval card into the transcript and pause
          const cardId = crypto.randomUUID();
          setMessages(prev => [
            ...prev,
            { id: cardId, role: "approval", name, input, decided: null },
          ]);
          // Bind the card id to the in-flight request so we can mark decided
          const decision = await new Promise((resolve) => {
            setPendingApproval({ name, input, resolve, uiId: cardId });
          });
          return decision;
        },
        onEvent: (ev) => {
          if (ev.type === "text_delta") {
            setMessages(prev => prev.map(m =>
              m.id === assistantId ? { ...m, text: m.text + ev.text } : m
            ));
          } else if (ev.type === "tool_use_start") {
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, events: [...m.events, { type: "tool", id: ev.id, name: ev.name, status: "running" }] }
                : m
            ));
          } else if (ev.type === "tool_result") {
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              return {
                ...m,
                events: m.events.map(e =>
                  e.id === ev.id ? { ...e, status: ev.is_error ? "error" : "done", result: ev.content } : e
                ),
              };
            }));
            // Open a fresh assistant bubble for the next text round
            // (the next turn starts a new assistant_start)
          } else if (ev.type === "tool_denied") {
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              return {
                ...m,
                events: m.events.map(e => e.id === ev.id ? { ...e, status: "denied" } : e),
              };
            }));
          } else if (ev.type === "assistant_start") {
            // After a tool round, model speaks again — open a new bubble
            setMessages(prev => {
              const last = prev[prev.length - 1];
              // If last is the streaming assistant we already opened and it has no text yet, reuse it
              if (last && last.role === "assistant" && !last.text && !last.events.length) return prev;
              return [...prev, { id: crypto.randomUUID(), role: "assistant", text: "", events: [], _continuation: true }];
            });
          }
        },
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        const msg = err.message || String(err);
        setMessages(prev => [
          ...prev,
          { id: crypto.randomUUID(), role: "system", text: `Error: ${msg}` },
        ]);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={`fixed inset-0 backdrop z-40 ${fullPage ? "hidden" : ""}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={fullPage
              ? "fixed inset-0 z-30 w-full bg-pronto-page flex safe-top overflow-hidden pb-[68px] md:pb-0"
              : "fixed inset-y-0 right-0 z-50 w-full md:w-[680px] bg-pronto-page shadow-2xl flex safe-top safe-bottom overflow-hidden"}
            initial={fullPage ? false : { x: "100%" }}
            animate={fullPage ? undefined : { x: 0 }}
            exit={fullPage ? undefined : { x: "100%" }}
            transition={fullPage ? undefined : { type: "spring", damping: 28, stiffness: 280 }}
          >
            <ChatList
              conversations={conversations}
              activeId={activeId}
              show={showList}
              onSelect={handleSelect}
              onNew={handleNew}
              onDelete={handleDelete}
              onRename={handleRename}
              onHide={() => setShowList(false)}
            />

            <div className="flex-1 flex flex-col min-w-0">
            <header className={`flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--p-sep)] ${fullPage ? "pr-[96px] md:pr-4" : ""}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                {/* ☰ conversations menu — opens the chat list (persistent rail on desktop) */}
                <button
                  onClick={() => setShowList(true)}
                  className="md:hidden h-7 pl-1.5 pr-2.5 rounded-[8px] bg-[var(--pg-6)] border border-[var(--pg-border)] flex items-center gap-1 text-pronto-navy shrink-0"
                  aria-label="Saved chats"
                >
                  <Menu size={15} />
                  <span className="font-sf text-[12px] font-semibold leading-none">Saved</span>
                </button>
                {fullPage && (
                  <button
                    onClick={handleNew}
                    className="w-[30px] h-[30px] rounded-full bg-[linear-gradient(180deg,#55617E,#3F4A63)] text-white grid place-items-center shadow-pronto-btn shrink-0"
                    aria-label="New chat"
                    title="New chat"
                  >
                    <Plus size={16} strokeWidth={2.4} />
                  </button>
                )}
                <div className="min-w-0">
                  <h3 className="font-round font-bold text-[18px] leading-tight text-pronto-ink truncate">
                    {fullPage ? "Ask Pronto" : activeTitle}
                  </h3>
                  <CostMeter meter={meter} />
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!fullPage && (
                  <>
                    <button
                      onClick={() => { window.location.hash = "chat"; }}
                      className="text-pronto-text-3 hover:text-pronto-ink p-1.5 rounded-lg hover:bg-[var(--pg-7)]"
                      aria-label="Expand chat to full page"
                      title="Expand to full page"
                    >
                      <Maximize2 size={16} />
                    </button>
                    <button
                      onClick={() => window.open(`${window.location.pathname}#chat`, "_blank", "noopener")}
                      className="text-pronto-text-3 hover:text-pronto-ink p-1.5 rounded-lg hover:bg-[var(--pg-7)]"
                      aria-label="Open chat in a new tab"
                      title="Open in new tab"
                    >
                      <ExternalLink size={16} />
                    </button>
                  </>
                )}
                {messages.length > 0 && (
                  <button
                    onClick={() => {
                      if (busy) return;
                      if (window.confirm("Clear this conversation?")) {
                        onClear();
                      }
                    }}
                    disabled={busy}
                    className="text-pronto-text-3 hover:text-pronto-navy disabled:opacity-30 p-1.5 rounded-lg hover:bg-[var(--pg-7)]"
                    aria-label="Clear chat"
                    title="Clear chat"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                {!fullPage && (
                  <button
                    onClick={handleNew}
                    className="w-[30px] h-[30px] rounded-full bg-[linear-gradient(180deg,#55617E,#3F4A63)] text-white grid place-items-center shadow-pronto-btn shrink-0"
                    aria-label="New chat"
                    title="New chat"
                  >
                    <Plus size={16} strokeWidth={2.4} />
                  </button>
                )}
                {!fullPage && (
                  <button
                    onClick={onClose}
                    className="text-pronto-text-3 hover:text-pronto-ink p-1.5 rounded-lg hover:bg-[var(--pg-7)] flex items-center gap-1"
                    aria-label="Close chat"
                    title="Close chat"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </header>


            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
              {messages.length === 0 && <ChatEmpty />}
              {messages.map(msg => (
                <ChatMessage key={msg.id} msg={msg} onDecide={resolveApproval} />
              ))}
              {busy && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex items-center gap-2 text-pronto-text-3 text-xs px-1">
                  <Loader2 size={12} className="animate-spin" /> thinking
                </div>
              )}
            </div>

            <footer className="p-3 pb-4">
              <div className={`${GLASS_INPUT} flex items-end gap-2 rounded-[20px] pl-4 pr-1.5 py-1.5`}>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Messaggio…"
                  rows={1}
                  aria-label="Message"
                  className="flex-1 resize-none bg-transparent border-0 py-1.5 text-[14px] text-pronto-ink placeholder:text-pronto-text-3 focus:outline-none max-h-32"
                />
                <button
                  onClick={send}
                  disabled={busy || !input.trim()}
                  className="w-9 h-9 rounded-full bg-[linear-gradient(180deg,#55617E,#3F4A63)] text-white disabled:opacity-30 flex items-center justify-center shrink-0"
                  aria-label="Send"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={17} strokeWidth={2.4} />}
                </button>
              </div>
            </footer>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Compact "3m ago" / "2d ago" stamp for the conversation list.
function relativeTime(ts) {
  if (!ts) return "";
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return `${Math.max(1, Math.floor(day / 30))}mo ago`;
}

// Left rail: the conversation list + "New chat". Always visible on desktop
// (md:flex); on mobile it slides in as an overlay toggled by `show`.
function ChatList({ conversations, activeId, show, onSelect, onNew, onDelete, onRename, onHide }) {
  const sorted = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);
  return (
    <>
      {show && <div className="md:hidden absolute inset-0 z-10 bg-black/25" onClick={onHide} aria-hidden />}
      <aside
        className={`${show ? "flex" : "hidden"} md:flex absolute md:relative inset-y-0 left-0 z-20 w-[78%] max-w-[280px] md:w-[220px] shrink-0 flex-col bg-pronto-page border-r border-[var(--p-sep)] safe-top safe-bottom`}
      >
        <div className="p-3">
          <button
            onClick={onNew}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-[14px] bg-[linear-gradient(180deg,#55617E,#3F4A63)] text-white text-sm font-semibold font-sf shadow-pronto-btn"
          >
            <Plus size={16} strokeWidth={2.4} /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
          {sorted.length === 0 && (
            <p className="text-xs text-pronto-text-3 text-center px-3 py-6">No conversations yet.</p>
          )}
          {sorted.map(c => (
            <ChatListItem
              key={c.id}
              conv={c}
              active={c.id === activeId}
              onSelect={() => onSelect(c.id)}
              onDelete={() => onDelete(c.id)}
              onRename={onRename}
            />
          ))}
        </div>
      </aside>
    </>
  );
}

function ChatListItem({ conv, active, onSelect, onDelete, onRename }) {
  const title = conv.title || "New chat";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef(null);

  const startEdit = () => { setDraft(conv.title || ""); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (conv.title || "")) onRename(conv.id, next);
  };

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  if (editing) {
    return (
      <div className="rounded-[10px] bg-[var(--pg-7)] px-2 py-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          maxLength={80}
          placeholder="Chat name"
          className="w-full bg-pronto-page border border-[var(--p-sep-12)] rounded-md px-2 py-1 text-sm text-pronto-ink focus:outline-none focus:border-pronto-navy"
        />
      </div>
    );
  }

  return (
    <div className={`group relative rounded-[10px] transition-colors ${active ? "bg-[rgba(63,74,99,0.12)]" : "hover:bg-[var(--pg-7)]"}`}>
      <button onClick={onSelect} onDoubleClick={startEdit} className="w-full text-left pl-3 pr-14 py-2 min-w-0 block">
        <div className={`truncate text-sm font-sf ${active ? "text-pronto-ink font-semibold" : "text-pronto-text-2"}`}>{title}</div>
        <div className="text-[10px] text-pronto-text-3 mt-0.5">{relativeTime(conv.updatedAt)}</div>
      </button>
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          onClick={startEdit}
          className="p-1.5 rounded-md text-pronto-text-3 hover:text-pronto-ink hover:bg-[var(--pg)]"
          aria-label="Rename chat"
          title="Rename chat"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-md text-pronto-text-3 hover:text-pronto-navy hover:bg-[var(--pg)]"
          aria-label="Delete chat"
          title="Delete chat"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function CostMeter({ meter }) {
  if (!meter || meter.calls === 0) {
    return (
      <div className="font-sf text-[10px] uppercase tracking-[0.18em] text-pronto-text-3 mt-0.5">
        pantry · recipes · coffee
      </div>
    );
  }
  const cacheRatio = meter.cacheReadTokens && meter.tokens
    ? Math.round((meter.cacheReadTokens / (meter.tokens + meter.cacheReadTokens)) * 100)
    : 0;
  return (
    <div
      className="font-sf text-[10px] uppercase tracking-[0.14em] text-pronto-text-3 mt-0.5 flex items-center gap-2 tabular"
      title={`${meter.calls} call${meter.calls === 1 ? "" : "s"} · ${meter.tokens.toLocaleString()} non-cached tokens · ${meter.cacheReadTokens.toLocaleString()} cache reads`}
    >
      <span className="text-pronto-text-2">{formatTokens(meter.tokens)}</span>
      <span className="text-pronto-text-4">·</span>
      <span className="text-pronto-navy">{formatCostCents(meter.costCents)}</span>
      {cacheRatio >= 30 && (
        <>
          <span className="text-pronto-text-4">·</span>
          <span className="text-[#1C7268] normal-case" title="Share of input tokens served from prompt cache">{cacheRatio}% cached</span>
        </>
      )}
    </div>
  );
}

// Empty state = the prototype's verbatim opener bubble.
function ChatEmpty() {
  return (
    <ChatBubble from="assistant">
      <span className="font-semibold">Pronto!</span> Cosa cuciniamo?{" "}
      <span className="text-pronto-text-3">(What are we cooking?)</span>
    </ChatBubble>
  );
}

function ChatMessage({ msg, onDecide }) {
  if (msg.role === "user") {
    return (
      <ChatBubble from="user">
        <span className="whitespace-pre-wrap">{msg.text}</span>
      </ChatBubble>
    );
  }

  if (msg.role === "system") {
    return (
      <div className="text-center text-xs text-pronto-text-3 italic">{msg.text}</div>
    );
  }

  if (msg.role === "approval") {
    return <ApprovalCard msg={msg} onDecide={onDecide} />;
  }

  // assistant — tool-step chips (left, above) then the glass bubble.
  return (
    <div className="space-y-1.5">
      {msg.events && msg.events.length > 0 && (
        <div className="space-y-0.5">
          {msg.events.map(ev => <ToolEvent key={ev.id} event={ev} />)}
        </div>
      )}
      {msg.text && (
        <ChatBubble from="assistant">
          <span className="whitespace-pre-wrap">{msg.text}</span>
        </ChatBubble>
      )}
    </div>
  );
}

// Inline tool-step chip — "✓ searched recipes" (prototype). Present-tense while
// running, concise past-tense once done.
function ToolEvent({ event }) {
  const done = event.status === "done";
  const label = (done && TOOL_LABELS_DONE[event.name]) || TOOL_LABELS[event.name] || event.name;
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-sf text-pronto-text-3 px-1">
      {event.status === "running" && <Loader2 size={11} className="animate-spin" />}
      {event.status === "done" && <Check size={11} className="text-[#1C7268]" strokeWidth={2.6} />}
      {event.status === "error" && <X size={11} className="text-pronto-warn" />}
      {event.status === "denied" && <X size={11} className="text-pronto-text-3" />}
      {!event.status && <Wrench size={11} />}
      <span>
        {label}
        {event.status === "denied" && " (denied)"}
        {event.status === "error" && " (failed)"}
      </span>
    </div>
  );
}

function ApprovalCard({ msg, onDecide }) {
  if (msg.name === "save_recipe") return <RecipeSaveCard msg={msg} onDecide={onDecide} />;
  const decided = msg.decided;
  const confirmLabel = msg.name === "add_shopping_item" ? "Sì, add" : "Sì";
  return (
    <div className={`${GLASS_INPUT} border-[rgba(63,74,99,0.3)] rounded-[14px] p-3 w-[88%]`}>
      <div className="font-sf text-[10px] font-bold uppercase tracking-[0.1em] text-pronto-navy mb-1">
        conferma
      </div>
      <div className="font-sf text-[14px] text-pronto-ink leading-snug mb-3">
        {describeWrite(msg.name, msg.input)}
      </div>
      {decided == null ? (
        <div className="flex gap-2">
          <button
            onClick={() => onDecide(true)}
            className="flex-1 h-9 rounded-[14px] bg-[linear-gradient(180deg,#55617E,#3F4A63)] text-white shadow-pronto-btn font-sf font-semibold text-[14px]"
          >
            {confirmLabel}
          </button>
          <button
            onClick={() => onDecide(false)}
            className="flex-1 h-9 rounded-[14px] bg-[var(--pg-7)] border border-[var(--p-sep-12)] text-pronto-text-3 font-sf font-semibold text-[14px]"
          >
            No
          </button>
        </div>
      ) : (
        <div className="text-[11px] font-sf text-pronto-text-3">
          {decided === "approved" ? "✓ confermato" : "✗ annullato"}
        </div>
      )}
    </div>
  );
}

// Keep-or-discard card for a recipe the assistant just created. Shows the
// suggested name (editable) plus a compact preview; on Keep it resolves the
// approval with the possibly-edited title, which runChatTurn merges into the
// save_recipe input before writing to the archive.
function RecipeSaveCard({ msg, onDecide }) {
  const recipe = msg.input || {};
  const decided = msg.decided;
  const [title, setTitle] = useState(recipe.title || "Untitled recipe");

  const ingCount = (recipe.ingredients || []).length;
  const stepCount = (recipe.steps || []).length;
  const totalMin = recipe.total_min || 0;
  const tags = [...(recipe.cuisine || []), ...(recipe.mood_tags || [])].slice(0, 4);

  const meta = [
    ingCount ? `${ingCount} ingredient${ingCount === 1 ? "" : "s"}` : null,
    stepCount ? `${stepCount} step${stepCount === 1 ? "" : "s"}` : null,
    totalMin ? `${totalMin} min` : null,
  ].filter(Boolean);

  const keep = () => onDecide({ title: title.trim() || recipe.title || "Untitled recipe" });

  return (
    <div className={`${GLASS_INPUT} border-[rgba(46,158,143,0.35)] rounded-[14px] p-3 w-[88%]`}>
      <div className="flex items-start gap-2">
        <BookPlus size={14} className="text-[#1C7268] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-sf text-[10px] font-bold uppercase tracking-[0.1em] text-[#1C7268] mb-1.5">
            save to recipes
          </div>
          {decided == null ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Recipe name"
              className="w-full bg-pronto-page border border-[var(--p-sep-12)] rounded-[10px] px-2.5 py-1.5 text-sm font-medium text-pronto-ink focus:outline-none focus:border-[#1C7268]"
            />
          ) : (
            <div className="text-pronto-ink font-medium leading-snug">{title}</div>
          )}
          {(meta.length > 0 || tags.length > 0) && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-[11px] text-pronto-text-3">
              {meta.length > 0 && <span className="font-sf tabular">{meta.join(" · ")}</span>}
              {tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded-full bg-[var(--pg-7)] text-pronto-text-2 lowercase">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {decided == null ? (
        <div className="flex gap-2 mt-3 ml-6">
          <button
            onClick={keep}
            className="flex-1 h-9 rounded-[14px] bg-[#1C7268] text-white font-sf font-semibold text-[14px]"
          >
            Keep
          </button>
          <button
            onClick={() => onDecide(false)}
            className="flex-1 h-9 rounded-[14px] bg-[var(--pg-7)] border border-[var(--p-sep-12)] text-pronto-text-3 font-sf font-semibold text-[14px]"
          >
            Discard
          </button>
        </div>
      ) : (
        <div className="text-[11px] font-sf text-pronto-text-3 mt-2 ml-6">
          {decided === "approved" ? "✓ kept" : "✗ discarded"}
        </div>
      )}
    </div>
  );
}
